import { useMemo, useState } from 'react';
import { ONE_NIGHT_CENTRE_CARDS } from '@ringmaster/shared';
import type {
  OneNightPeekAction,
  OneNightSwapAction,
  PrivateView,
  PublicView,
  RoleId,
} from '@ringmaster/shared';
import { useRoom } from '../net/useRoom.js';
import { FixedActionBar, fixedPrimaryButtonStyle } from '../ui/FixedActionBar.js';
import { Glyph } from '../ui/Glyph.js';
import { HoloRoleCard } from '../ui/HoloRoleCard.js';
import { HowToPlayCard } from '../ui/HowToPlay.js';
import { PeekRevealCard } from '../ui/PeekRevealCard.js';
import { ONE_NIGHT_CARD_BACK, ROLE_PLAY_ORDER, roleCardDefFor } from '../ui/roleCardDefs.js';
import { RoleTally } from '../ui/WhisperRoleTally.js';
import { ROLE_GLYPH } from './roleGlyph.js';
import { ParticleLayer } from '../fx/particles/ParticleLayer.js';

/**
 * Every role One Night can deal into a seat or the centre, in order of play —
 * the shared list (ui/roleCardDefs.ts), so this strip, the lobby role gallery
 * and the how-to-play row can never fall out of order with each other.
 */
const ONE_NIGHT_TALLY_ROLES: RoleId[] = ROLE_PLAY_ORDER.onenight;

/**
 * Blood Moon: One Night — Hand (per-phone) screens. Routed from HandGameView
 * by gameId + phase (AI_SEATS_SPEC.md / packages/shared/src/state.ts). Same
 * crimson Blood Moon skin as the flagship game (this is a variant, not a new
 * game skin) — reuses HoloRoleCard/FixedActionBar/Glyph exactly.
 *
 * Screens:
 *  - OneNightDealScreen  (DEAL role reveal + the public pool panel)
 *  - OneNightWolvesScreen (ONENIGHT_NIGHT_ACTIONS: pack reveal + a lone wolf's centre peek)
 *  - OneNightSeerScreen  (ONENIGHT_NIGHT_ACTIONS: one player OR two centre cards)
 *  - OneNightSwapScreen  (ONENIGHT_NIGHT_ACTIONS [Thief AND Joker]: swap two cards)
 *  - OneNightDawnScreen  (ONENIGHT_DAWN: a content-free wake-up ritual beat)
 *  - OneNightResolveWaitScreen (ONENIGHT_RESOLVE: the vote rests before END)
 *
 * The sleep beats (ONENIGHT_NIGHTFALL, and every non-actor during a night sub-
 * phase), ONENIGHT_DISCUSS and ONENIGHT_VOTE reuse the EXISTING Blood Moon
 * screens unchanged (NightSleepScreen / DiscussScreen / VoteScreen) — see
 * HandGameView.renderOneNightPhase. Nothing about their contracts differs.
 */

const NIGHT_BG = 'linear-gradient(180deg, #080808 0%, #320001 100%)';

/** Short haptic on a selection (guarded — many devices lack it). */
function tapHaptic() {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    try {
      navigator.vibrate(30);
    } catch {
      /* haptics unsupported */
    }
  }
}

// ─── DEAL — role reveal + the public pool ───────────────────────────────────

/**
 * OneNightDealScreen — mirrors Blood Moon's RoleRevealScreen (press-and-hold
 * HoloRoleCard, sticky pre-ready reveal, momentary post-ready peek) with two
 * One-Night-specific additions: the wolf pack panel (packmateIds, only when
 * non-empty — a LONE wolf sees none) and {@link OneNightPoolPanel}, since the
 * card pool is deliberately PUBLIC (see OneNightPublic doc comment) and gives
 * players their first anchor for the deduction.
 */
export function OneNightDealScreen({
  privateView,
  publicView,
  actionOffset = 0,
}: {
  privateView: PrivateView;
  publicView: PublicView;
  actionOffset?: number;
}) {
  const { send } = useRoom();
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const serverReady = privateView.action.type === 'READY' && privateView.action.done;
  const [localReady, setLocalReady] = useState(false);
  const isReady = serverReady || localReady;

  const def = roleCardDefFor('onenight', privateView.role);
  const packmateIds = privateView.oneNight?.packmateIds;
  const nameMap = useMemo(
    () => Object.fromEntries(publicView.players.map((p) => [p.id, p.name])),
    [publicView.players],
  );

  function handleReady() {
    setLocalReady(true);
    setRevealed(false);
    send({ type: 'READY' });
  }

  if (!onboardingDismissed) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #080808 0%, #140002 100%)',
          padding: 'var(--space-5)',
          paddingBottom: 120 + actionOffset,
        }}
      >
        <HowToPlayCard gameId="onenight" />
        <FixedActionBar offsetBottom={actionOffset}>
          <button type="button" className="rm-hover-pill" onClick={() => setOnboardingDismissed(true)} style={fixedPrimaryButtonStyle}>
            Got it — reveal my role
          </button>
        </FixedActionBar>
      </div>
    );
  }

  return (
    <div
      className="game-screen"
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'radial-gradient(120% 90% at 50% 0%, #1a0710 0%, #0a0305 55%, #060204 100%)',
        padding: 'var(--space-5)',
        paddingBottom: 110 + actionOffset,
        gap: 'var(--space-4)',
        overflow: 'hidden',
      }}
    >
      <ParticleLayer preset="goldDust" intensity={0.4} slot={0} />

      {/*
        Two-column on wide/short (landscape) aspect ratios — card left, pool
        (+ pack panel) right — so the pool doesn't force a tall stack that
        overflows a 16:9 frame. Stays the original stacked column on
        phone-portrait aspects (the media query never matches).
      */}
      <div
        className="onenight-deal-shell"
        style={{ position: 'relative', zIndex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-4)' }}
      >
        {/*
          The status label sits ABOVE the two-column row (not inside the card's
          own column) so that in the wide/landscape layout the card and the
          pool panel start at the SAME top edge — see .onenight-deal-row below.
        */}
        <div style={{ textAlign: 'center', minHeight: 48 }}>
          {revealed ? (
            <p style={{ fontFamily: 'var(--font-ui)', fontSize: 15, color: 'var(--text-secondary)', margin: 0 }}>
              {isReady ? 'Release to hide' : 'Your secret role — for tonight only'}
            </p>
          ) : isReady ? (
            <p style={{ fontFamily: 'var(--font-ui)', fontSize: 15, color: 'var(--text-secondary)', margin: 0, maxWidth: 260 }}>
              You&rsquo;re ready — hold the card to peek again.
            </p>
          ) : (
            <>
              <div style={{ fontFamily: 'var(--font-ui)', fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.15 }}>
                Tap and hold
              </div>
              <div style={{ fontFamily: 'var(--font-ui)', fontSize: 18, color: 'var(--text-secondary)', marginTop: 2 }}>
                to see your role
              </div>
            </>
          )}
        </div>

        <div
          className="onenight-deal-row"
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-4)', width: '100%' }}
        >
          <HoloRoleCard
            def={def}
            backSrc={ONE_NIGHT_CARD_BACK}
            revealed={revealed}
            onReveal={() => setRevealed(true)}
            onHoldEnd={() => { if (isReady) setRevealed(false); }}
            width="min(280px, 78vw, 32vh)"
          />

          {revealed && (
            <div className="onenight-deal-side" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-4)', width: '100%' }}>
              {packmateIds && packmateIds.length > 0 && (
                <div
                  style={{
                    width: '100%', maxWidth: 320,
                    background: 'rgba(128, 38, 54, 0.15)', border: '1px solid rgba(153,0,0,0.4)',
                    borderRadius: 'var(--radius-card)', padding: 'var(--space-4) var(--space-5)',
                  }}
                >
                  <div style={{ color: 'var(--accent-blood-end)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 'var(--space-3)' }}>
                    Your pack
                  </div>
                  {packmateIds.map((id) => (
                    <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: '8px 0', color: 'var(--text-primary)', fontSize: 15 }}>
                      <Glyph name="wolfHead" size={16} tone="blood" />
                      <span>{nameMap[id] ?? id}</span>
                    </div>
                  ))}
                </div>
              )}

              <OneNightPoolPanel publicView={publicView} />
            </div>
          )}
        </div>
      </div>

      {revealed && !isReady && (
        <FixedActionBar offsetBottom={actionOffset}>
          <button type="button" className="rm-hover-pill" onClick={handleReady} style={fixedPrimaryButtonStyle}>
            I&rsquo;m ready
          </button>
        </FixedActionBar>
      )}

      <style>{`
        @media (min-aspect-ratio: 4/3) {
          .onenight-deal-row { flex-direction: row !important; align-items: flex-start !important; justify-content: center !important; gap: var(--space-6) !important; }
          .onenight-deal-side { width: 320px !important; }
        }
      `}</style>
    </div>
  );
}

/**
 * OneNightPoolPanel — the pool is deliberately PUBLIC (OneNightPublic doc
 * comment): everyone knows which cards are in play, nobody knows where they
 * landed. Tallies `publicView.oneNight.pool` into role → count and renders
 * the same portrait-avatar strip as The Whisper's role tally ({@link RoleTally}
 * — see WhisperRoleTally.tsx) rather than Glyph icons, so every role display
 * in the game reads as one visual language. Shows all five One Night roles
 * (not just the ones present) so a table without a Thief in play still learns
 * the role exists, dimmed to 0 like Whisper's zero-count roles.
 *
 * `variant='bare'` drops the caption AND the card chrome, for a caller that
 * already supplies both — the roles popover (ui/OneNightRolesButton.tsx), whose
 * own title states the count. That keeps ONE pool row in the codebase instead
 * of a second near-copy inside the popover.
 */
export function OneNightPoolPanel({
  publicView,
  variant = 'panel',
}: {
  publicView: PublicView;
  variant?: 'panel' | 'bare';
}) {
  const pool = publicView.oneNight?.pool;
  if (!pool || pool.length === 0) return null;
  const counts: Partial<Record<RoleId, number>> = {};
  for (const r of pool) counts[r] = (counts[r] ?? 0) + 1;
  const centreCount = publicView.oneNight?.centreCount ?? ONE_NIGHT_CENTRE_CARDS;
  const bare = variant === 'bare';

  return (
    <div
      className="onenight-pool-panel"
      style={{
        position: 'relative', zIndex: 1, width: '100%',
        ...(bare
          ? null
          : {
              maxWidth: 320,
              background: 'rgba(255,255,255,0.03)', border: '1px solid var(--surface-stroke)',
              borderRadius: 'var(--radius-card)', padding: 'var(--space-4)',
            }),
      }}
    >
      {!bare && (
        <div style={{ color: 'var(--gold-5, #e6b866)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 'var(--space-3)', textAlign: 'center' }}>
          Tonight&rsquo;s pool ({centreCount} face-down)
        </div>
      )}
      <RoleTally gameId="onenight" roles={ONE_NIGHT_TALLY_ROLES} distribution={counts} accent="gold" />
    </div>
  );
}

// ─── ONENIGHT_NIGHT_ACTIONS (Werewolf) — pack reveal + a lone wolf's centre peek

/**
 * OneNightWolvesScreen — only rendered for a player DEALT the werewolf card
 * (see HandGameView.renderOneNightPhase; everyone else sees NightSleepScreen).
 * A paired wolf just sees their packmate (no action — the beat auto-advances).
 * A LONE wolf (packmateIds is an empty array) additionally gets one centre
 * peek via {@link CentrePeekPicker}.
 */
export function OneNightWolvesScreen({
  privateView,
  publicView,
  actionOffset = 0,
}: {
  privateView: PrivateView;
  publicView: PublicView;
  actionOffset?: number;
}) {
  const packmateIds = privateView.oneNight?.packmateIds ?? [];
  const isLone = packmateIds.length === 0;
  const action = privateView.action.type === 'ONENIGHT_PEEK' ? (privateView.action as OneNightPeekAction) : null;
  const nameMap = useMemo(
    () => Object.fromEntries(publicView.players.map((p) => [p.id, p.name])),
    [publicView.players],
  );

  return (
    <div
      className="game-screen"
      style={{
        position: 'relative', minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        background: 'linear-gradient(180deg, #0a0000 0%, #320001 100%)',
        padding: 'var(--space-6) var(--space-5)',
        paddingTop: 'max(var(--space-7), env(safe-area-inset-top, 0px) + var(--space-6))',
        paddingBottom: (isLone && action ? 176 : 40) + actionOffset,
        gap: 'var(--space-5)', overflow: 'hidden',
      }}
    >
      <ParticleLayer preset="bloodMotes" intensity={0.6} slot={0} />

      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-2)' }}>
          <Glyph name="wolfHead" size={44} tone="blood" />
        </div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, margin: 0, color: 'var(--text-primary)' }}>
          {isLone ? 'You are the only wolf' : 'Know your pack'}
        </h1>
        <p style={{ color: 'var(--blood-6, #ff5a4f)', fontSize: 14, margin: 'var(--space-3) 0 0', opacity: 0.85 }}>
          {isLone ? 'No packmate — but you may glimpse one centre card.' : 'Nobody else needs to know.'}
        </p>
      </div>

      {!isLone && (
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {packmateIds.map((id) => (
            <div
              key={id}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                background: 'rgba(153,0,0,0.15)', border: '1px solid rgba(153,0,0,0.4)',
                borderRadius: 14, padding: '14px var(--space-4)',
              }}
            >
              <Glyph name="wolfHead" size={18} tone="blood" />
              <span style={{ color: 'var(--text-primary)', fontSize: 16 }}>{nameMap[id] ?? id}</span>
            </div>
          ))}
        </div>
      )}

      {isLone && action && (
        <CentrePeekPicker
          action={action}
          candidates={[]}
          nameMap={nameMap}
          modeLabel="Turn over one centre card"
          actionOffset={actionOffset}
          oneNight={privateView.oneNight}
        />
      )}
    </div>
  );
}

// ─── ONENIGHT_NIGHT_ACTIONS (Seer) — one player OR two centre cards ─────────

/**
 * OneNightSeerScreen — the Seer's ONE choice: look at a single player, or
 * turn over TWO of the three centre cards. `CentrePeekPicker` renders both
 * halves and enforces the exclusivity (see its own doc comment).
 */
export function OneNightSeerScreen({
  privateView,
  publicView,
  actionOffset = 0,
}: {
  privateView: PrivateView;
  publicView: PublicView;
  actionOffset?: number;
}) {
  const action = privateView.action as OneNightPeekAction;
  const nameMap = useMemo(
    () => Object.fromEntries(publicView.players.map((p) => [p.id, p.name])),
    [publicView.players],
  );

  return (
    <div
      className="game-screen"
      style={{
        position: 'relative', minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        background: 'linear-gradient(180deg, #080808 0%, #160b26 100%)',
        padding: 'var(--space-6) var(--space-5)',
        paddingTop: 'max(var(--space-7), env(safe-area-inset-top, 0px) + var(--space-6))',
        paddingBottom: 176 + actionOffset,
        gap: 'var(--space-5)', overflow: 'hidden',
      }}
    >
      <ParticleLayer preset="arcanaWisps" intensity={0.7} slot={0} />

      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-2)' }}>
          <Glyph name="seerEye" size={44} tone="arcana" />
        </div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, margin: 0, color: 'var(--text-primary)' }}>
          One truth, your choice
        </h1>
        <p style={{ color: 'var(--arcana-4, #c9b6ff)', fontSize: 14, margin: 'var(--space-3) 0 0', opacity: 0.85 }}>
          A player&rsquo;s card, or two from the centre — never both.
        </p>
      </div>

      <CentrePeekPicker
        action={action}
        candidates={action.candidates}
        nameMap={nameMap}
        modeLabel="Turn over two centre cards"
        actionOffset={actionOffset}
        accent="arcana"
        oneNight={privateView.oneNight}
      />
    </div>
  );
}

/**
 * CentrePeekPicker — shared peek UI for both One Night beats. Two mutually
 * exclusive modes: pick ONE seated player (only offered when `candidates` is
 * non-empty — the Seer), or turn over `action.centreAllowance` centre slots
 * (a lone wolf: 1; the Seer: 2, of the {@link ONE_NIGHT_CENTRE_CARDS} face-down).
 * Submits {@link OneNightPeekAction}'s ONENIGHT_PEEK intent, then shows the
 * revealed role(s) from `privateView.oneNight.seenPlayers`/`seenCentre` once
 * the server confirms (tracked locally so the reveal survives a re-render
 * before the private view catches up).
 */
function CentrePeekPicker({
  action,
  candidates,
  nameMap,
  modeLabel,
  actionOffset,
  accent = 'blood',
  oneNight,
}: {
  action: OneNightPeekAction;
  candidates: string[];
  nameMap: Record<string, string>;
  modeLabel: string;
  actionOffset: number;
  accent?: 'blood' | 'arcana';
  oneNight?: PrivateView['oneNight'];
}) {
  const { send } = useRoom();
  const [mode, setMode] = useState<'player' | 'centre' | null>(candidates.length > 0 ? null : 'centre');
  const [targetId, setTargetId] = useState<string | null>(null);
  const [centreSlots, setCentreSlots] = useState<number[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const tint = accent === 'arcana' ? 'var(--arcana-3, #8b5cf6)' : 'var(--blood-4, #990000)';
  const glow = accent === 'arcana' ? '0 0 24px rgba(139,92,246,0.5)' : 'var(--rim-blood, 0 0 24px rgba(153,0,0,0.55))';

  const seenPlayers = oneNight?.seenPlayers ?? {};
  const seenCentre = oneNight?.seenCentre ?? {};
  const centreCount = ONE_NIGHT_CENTRE_CARDS;

  function toggleCentreSlot(i: number) {
    setCentreSlots((prev) => {
      if (prev.includes(i)) return prev.filter((x) => x !== i);
      if (prev.length >= action.centreAllowance) return prev; // full
      tapHaptic();
      return [...prev, i];
    });
  }

  function handleConfirm() {
    if (mode === 'player' && targetId) {
      send({ type: 'ONENIGHT_PEEK', targetId, centre: [] });
    } else if (mode === 'centre' && centreSlots.length === action.centreAllowance) {
      send({ type: 'ONENIGHT_PEEK', targetId: null, centre: centreSlots });
    } else {
      return;
    }
    setSubmitted(true);
  }

  function handleSkip() {
    send({ type: 'ONENIGHT_PEEK', targetId: null, centre: [] });
    setSubmitted(true);
  }

  const canConfirm =
    (mode === 'player' && !!targetId) || (mode === 'centre' && centreSlots.length === action.centreAllowance);

  if (submitted || action.done) {
    const revealedPlayerRole = targetId ? seenPlayers[targetId] : null;
    const revealedCentreRoles = centreSlots.map((i) => seenCentre[String(i)]).filter(Boolean) as RoleId[];
    const sawSomething = revealedPlayerRole || revealedCentreRoles.length > 0;
    return (
      <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-4)', textAlign: 'center' }}>
        {sawSomething ? (
          <>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>
              {revealedPlayerRole ? `${nameMap[targetId!] ?? targetId} is…` : 'You saw…'}
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-4)', justifyContent: 'center', flexWrap: 'wrap' }}>
              {revealedPlayerRole && <PeekRevealCard role={revealedPlayerRole} width="min(160px, 42vw)" />}
              {revealedCentreRoles.map((r, i) => (
                <PeekRevealCard key={i} role={r} width="min(130px, 32vw)" />
              ))}
            </div>
          </>
        ) : (
          <p style={{ color: 'var(--text-secondary)', fontSize: 15, margin: 0 }}>You looked away. Sleep now.</p>
        )}
      </div>
    );
  }

  return (
    <>
      {mode === null && (
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', gap: 'var(--space-3)' }}>
          <button type="button" className="rm-hover" onClick={() => setMode('player')} style={choiceBtnStyle(tint)}>
            Look at a player
          </button>
          <button type="button" className="rm-hover" onClick={() => setMode('centre')} style={choiceBtnStyle(tint)}>
            {modeLabel}
          </button>
        </div>
      )}

      {mode === 'player' && (
        <div style={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {candidates.map((id) => {
              const isSelected = targetId === id;
              return (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => setTargetId(id)}
                    className="rm-hover-row"
                    style={{
                      width: '100%', minHeight: 52, textAlign: 'left', padding: '14px var(--space-4)', borderRadius: 14,
                      border: isSelected ? `1px solid ${tint}` : '1px solid var(--surface-stroke)',
                      background: isSelected ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.04)',
                      color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'var(--font-ui)', fontSize: 16,
                    }}
                  >
                    {nameMap[id] ?? id}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {mode === 'centre' && (
        <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0 }}>
            Pick {action.centreAllowance} of {centreCount} face-down cards
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
            {Array.from({ length: centreCount }, (_, i) => i).map((i) => {
              const isSelected = centreSlots.includes(i);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleCentreSlot(i)}
                  aria-pressed={isSelected}
                  style={{
                    width: 64, height: 92, borderRadius: 10, overflow: 'hidden',
                    border: isSelected ? `2px solid ${tint}` : '1px solid var(--surface-stroke)',
                    backgroundImage: `url(${ONE_NIGHT_CARD_BACK})`, backgroundSize: 'cover', backgroundPosition: 'center',
                    boxShadow: isSelected ? glow : '0 2px 10px rgba(0,0,0,0.5)',
                    opacity: isSelected ? 1 : 0.82,
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0 0 6px',
                    transition: 'all var(--duration-fast) var(--ease-standard)',
                  }}
                >
                  <span style={{ fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 600, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.85)' }}>
                    {i + 1}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {mode !== null && (
        <FixedActionBar offsetBottom={actionOffset}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {candidates.length > 0 && (
              <button type="button" className="rm-hover" onClick={() => { setMode(null); setTargetId(null); setCentreSlots([]); }} style={{ ...fixedPrimaryButtonStyle, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--surface-stroke)' }}>
                Choose differently
              </button>
            )}
            <button
              type="button"
              className="rm-hover"
              disabled={!canConfirm}
              onClick={handleConfirm}
              style={{
                ...fixedPrimaryButtonStyle,
                background: canConfirm ? `linear-gradient(135deg, ${tint} 0%, ${tint} 100%)` : 'rgba(255,255,255,0.1)',
                color: canConfirm ? '#fff' : 'var(--text-tertiary)',
                cursor: canConfirm ? 'pointer' : 'not-allowed',
              }}
            >
              Confirm
            </button>
            <button type="button" onClick={handleSkip} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: 13, fontFamily: 'var(--font-ui)', cursor: 'pointer', padding: 8 }}>
              Skip — look at nothing
            </button>
          </div>
        </FixedActionBar>
      )}
    </>
  );
}

function choiceBtnStyle(tint: string): React.CSSProperties {
  return {
    flex: 1, minHeight: 64, borderRadius: 14, border: `1px solid ${tint}`,
    background: 'rgba(255,255,255,0.04)', color: 'var(--text-primary)',
    fontFamily: 'var(--font-ui)', fontSize: 15, fontWeight: 600, cursor: 'pointer',
    padding: 'var(--space-3)',
  };
}

// ─── ONENIGHT_NIGHT_ACTIONS (Thief + Joker) — swap two cards ────────────────

/**
 * OneNightSwapScreen — shared by the Thief (`action.picks === 1`: they pick
 * ONE other player and swap with THEM, then look) and the Joker
 * (`action.picks === 2`: they pick TWO other players and swap those two
 * blind, learning nothing). BOTH act in the same parallel beat, so everything
 * on this screen is driven by the action's shape (`picks`), never by the phase.
 * Submits {@link OneNightSwapAction}'s ONENIGHT_SWAP intent — `aId` is always
 * self for the Thief, never for the Joker (the engine enforces this; the client
 * only ever sends the shape its own role produces). Tap order between the two
 * swappers is irrelevant: the engine records intents and applies them at the
 * end of the beat in the canonical order, Thief before Joker.
 */
export function OneNightSwapScreen({
  privateView,
  publicView,
  actionOffset = 0,
}: {
  privateView: PrivateView;
  publicView: PublicView;
  actionOffset?: number;
}) {
  const { send } = useRoom();
  const action = privateView.action as OneNightSwapAction;
  const isThief = action.picks === 1;
  const [picked, setPicked] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const nameMap = useMemo(
    () => Object.fromEntries(publicView.players.map((p) => [p.id, p.name])),
    [publicView.players],
  );
  const stolenRole = privateView.oneNight?.stolenRole;

  // Selection stays freely changeable until Confirm: tapping a NEW target
  // when already at the pick limit slides the window (drops the oldest pick)
  // instead of locking out further taps — a lone Thief pick (action.picks===1)
  // simply replaces the prior choice.
  function toggle(id: string) {
    setPicked((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      tapHaptic();
      if (prev.length >= action.picks) return [...prev.slice(1), id];
      return [...prev, id];
    });
  }

  function handleConfirm() {
    if (picked.length !== action.picks) return;
    if (isThief) {
      send({ type: 'ONENIGHT_SWAP', aId: privateView.playerId, bId: picked[0]! });
    } else {
      send({ type: 'ONENIGHT_SWAP', aId: picked[0]!, bId: picked[1]! });
    }
    setSubmitted(true);
  }

  function handleSkip() {
    // Same-id-twice is the documented explicit SKIP form.
    send({ type: 'ONENIGHT_SWAP', aId: privateView.playerId, bId: privateView.playerId });
    setSubmitted(true);
  }

  const canConfirm = picked.length === action.picks;

  if (submitted || action.done) {
    return (
      <div
        style={{
          minHeight: '100dvh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
          background: NIGHT_BG, gap: 'var(--space-5)', padding: 'var(--space-5)', textAlign: 'center',
        }}
      >
        {isThief && stolenRole ? (
          <>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>You are now…</p>
            <PeekRevealCard role={stolenRole} width="min(220px, 60vw)" />
          </>
        ) : (
          <>
            <Glyph name={ROLE_GLYPH[privateView.role]} size={44} tone="bone" />
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--text-primary)', margin: 0 }}>
              {/* Real apostrophe — this is a JS string, not JSX text, so an HTML
                  entity here would render literally as "You&rsquo;ll". */}
              {isThief ? 'You kept your own card.' : 'Done. You’ll never know what changed.'}
            </p>
          </>
        )}
        <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0 }}>Sleep now.</p>
      </div>
    );
  }

  const eligible = publicView.players.filter((p) => p.id !== privateView.playerId);

  return (
    <div
      className="game-screen"
      style={{
        position: 'relative', minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        background: NIGHT_BG,
        padding: 'var(--space-6) var(--space-5)',
        paddingTop: 'max(var(--space-7), env(safe-area-inset-top, 0px) + var(--space-6))',
        paddingBottom: 176 + actionOffset,
        gap: 'var(--space-5)', overflow: 'hidden',
      }}
    >
      <ParticleLayer preset="bloodMotes" intensity={0.5} slot={0} />

      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        {/* The acting role's own glyph (ROLE_GLYPH), not a Thief-vs-Joker
            ternary: the Joker and the real Seer are awake in the SAME beat now,
            so they must not wear the same icon. */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-2)' }}>
          <Glyph name={ROLE_GLYPH[privateView.role]} size={40} tone="bone" />
        </div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, margin: 0, color: 'var(--text-primary)' }}>
          {isThief ? 'Take another’s fate' : 'Twist two fates, blind'}
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 'var(--space-3) 0 0', opacity: 0.85 }}>
          {isThief
            ? 'Swap your card with theirs, then look at what you took.'
            : 'Swap two OTHER players’ cards. You will never learn what changed.'}
        </p>
      </div>

      <div style={{ position: 'relative', zIndex: 1, flex: 1 }}>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {eligible.map((p) => {
            const isSelected = picked.includes(p.id);
            const order = picked.indexOf(p.id);
            return (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => toggle(p.id)}
                  className="rm-hover-row"
                  style={{
                    width: '100%', minHeight: 52, textAlign: 'left', padding: '14px var(--space-4)', borderRadius: 14,
                    border: isSelected ? '1px solid rgba(255,255,255,0.6)' : '1px solid var(--surface-stroke)',
                    background: isSelected ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.04)',
                    color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'var(--font-ui)', fontSize: 16,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}
                >
                  <span>{p.name}</span>
                  {isSelected && !isThief && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>#{order + 1}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <FixedActionBar offsetBottom={actionOffset}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <button
            type="button"
            className="rm-hover"
            disabled={!canConfirm}
            onClick={handleConfirm}
            style={{
              ...fixedPrimaryButtonStyle,
              background: canConfirm ? 'linear-gradient(135deg, var(--blood-3, #802636) 0%, var(--blood-4, #990000) 100%)' : 'rgba(255,255,255,0.1)',
              color: canConfirm ? '#fff' : 'var(--text-tertiary)',
              cursor: canConfirm ? 'pointer' : 'not-allowed',
            }}
          >
            {isThief
              ? picked[0]
                ? `Swap with ${nameMap[picked[0]] ?? picked[0]}`
                : 'Choose someone'
              : picked.length === 2
                ? `Swap ${nameMap[picked[0]!] ?? picked[0]} ↔ ${nameMap[picked[1]!] ?? picked[1]}`
                : `Choose ${2 - picked.length} more`}
          </button>
          <button type="button" onClick={handleSkip} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: 13, fontFamily: 'var(--font-ui)', cursor: 'pointer', padding: 8 }}>
            Skip — swap nothing
          </button>
        </div>
      </FixedActionBar>
    </div>
  );
}

// ─── ONENIGHT_DAWN — content-free wake ritual ───────────────────────────────

/**
 * OneNightDawnScreen — unlike Blood Moon's DawnScreen, One Night NEVER has a
 * night kill (see OneNightDawnEvent doc comment) — a pure "the swapping is
 * done, open your eyes" beat. Nobody's card is revealed here; a swapped
 * player still believes they are whatever they were dealt.
 */
export function OneNightDawnScreen() {
  return (
    <div
      className="game-screen"
      style={{
        position: 'relative', minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center',
        background: 'linear-gradient(180deg, #080808 0%, #0d0507 100%)',
        gap: 'var(--space-5)', padding: 'var(--space-5)', overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '38%', pointerEvents: 'none',
          background: 'linear-gradient(180deg, rgba(193,102,60,0.22) 0%, rgba(125,59,51,0.12) 45%, rgba(0,0,0,0) 100%)',
        }}
      />
      <ParticleLayer preset="goldDust" intensity={0.5} slot={0} />
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'center' }}>
        <Glyph name="dawnBird" size={44} tone="gold" />
      </div>
      <p style={{ position: 'relative', zIndex: 1, fontFamily: 'var(--font-display)', fontSize: 24, margin: 0, color: '#fff', textAlign: 'center' }}>
        Dawn breaks
      </p>
      <p style={{ position: 'relative', zIndex: 1, color: 'var(--text-secondary)', fontSize: 15, margin: 0, textAlign: 'center', maxWidth: 280 }}>
        Open your eyes. Whatever changed in the night, nobody will say.
      </p>
    </div>
  );
}

// ─── ONENIGHT_RESOLVE — the vote rests before END ───────────────────────────

/**
 * OneNightResolveWaitScreen — a short rest after the simultaneous vote, before
 * END. Unlike Blood Moon's RESOLVE_VOTE, One Night's full truth (centre cards,
 * dealt vs. final roles, who died) is reserved for END
 * (`OneNightPublic.reveal`, null until then) — so this beat stays a calm
 * holding screen, not a reveal.
 */
export function OneNightResolveWaitScreen({ caption }: { caption?: string }) {
  return (
    <div
      style={{
        position: 'relative', minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center',
        background: 'linear-gradient(180deg, #080808 0%, #0d0507 100%)',
        gap: 'var(--space-4)', padding: 'var(--space-6)', textAlign: 'center',
      }}
    >
      <div style={{ animation: 'onenight-resolve-pulse 2.2s ease-in-out infinite' }}>
        <Glyph name="scales" size={48} tone="bone" />
      </div>
      <p style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'rgba(255,255,255,0.85)', margin: 0 }}>
        The village has spoken&hellip;
      </p>
      {caption && (
        <p style={{ color: 'var(--text-tertiary)', fontSize: 14, margin: 0, maxWidth: 280 }}>{caption}</p>
      )}
      <style>{`
        @keyframes onenight-resolve-pulse { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { @keyframes onenight-resolve-pulse { 0%, 100% { opacity: 0.8; } } }
      `}</style>
    </div>
  );
}
